import ky, { type Options } from 'ky';
import { type StateCreator } from 'zustand';
import { LoginThemes } from '../hooks/useDefaultLoginTheme';
import { apiEndpoints } from '../../lib/apiEndpoints/apiEndpoints';

export type LoginSettings = {
  portalEnabled: boolean;
  tenantCode: string;
  signInGuidance: boolean;
  signInGuidanceText: string | null | undefined;
  authenticationMode: 'OTPV2' | 'SSO';
  authenticationParameters: {
    app: string;
    mode: string;
    clientId: string;
    userPoolId: string;
    accessScope: string;
    authDomain: string;
    idP: string;
  } | null;
  backgroundUrl: string | null | undefined;
  logoUrl: string | null | undefined;
  signInTheme: LoginThemes | null | undefined;
};

type AuthStoreState = {
  isFetching: boolean;
  tenantId?: string;
  error: string | null;
  loginSettings: LoginSettings | null;
};

type AuthStoreActions = {
  init: (tenantId?: string) => void;
};

export type AuthStore = AuthStoreState & AuthStoreActions;

export const auth: StateCreator<AuthStore> = (set, get) => {
  // Identifies the latest init() call. A response for an earlier call - possibly for another
  // tenant - must not overwrite the settings of the call that superseded it (HRI2-16956).
  let latestInitId = 0;

  const store: AuthStore = {
    isFetching: false,
    error: null,
    loginSettings: null,
    async init(tenantId) {
      console.log('Initializing');
      const initId = ++latestInitId;
      const isLatestInit = () => initId === latestInitId;

      // Settings belong to one tenant, and this store is persisted per tab. Keeping another
      // tenant's settings while this tenant's are fetched let the login screen mint the OAuth
      // state - and pick the Cognito domain - for the wrong tenant (HRI2-16956).
      if (get().tenantId !== tenantId) {
        set({ loginSettings: null });
      }

      set({ tenantId, isFetching: true });
      try {
        const kyOpts: Options = {
          ...(tenantId ? { headers: { 'OT-Customer-Id': tenantId } } : {}),
        };
        const apiBaseURL = apiEndpoints.getAPIEndpoint('root');

        if (apiBaseURL == null) {
          throw new Error('endpoint "root" is not defined');
        }
        const loginSettings = await ky.get(`${apiBaseURL}/login/settings`, kyOpts).json<LoginSettings>();
        if (isLatestInit()) {
          set({ loginSettings });
        }
      } catch {
        if (!isLatestInit()) {
          return;
        }
        set({
          loginSettings: {
            portalEnabled: false,
            tenantCode: tenantId?.toUpperCase() || 'DEMO',
            signInGuidance: false,
            signInGuidanceText: undefined,
            authenticationMode: 'OTPV2',
            authenticationParameters: null,
            backgroundUrl: undefined,
            logoUrl: undefined,
            signInTheme: undefined,
          },
        });
      } finally {
        if (isLatestInit()) {
          set({
            isFetching: false,
          });
        }
      }
    },
  };
  return store;
};
